/**
 * Pharmaceuticals Industry Configuration
 *
 * Supports: Bioreactors, Tablet Presses, Coating Machines, Filling Lines, Autoclaves, HVAC Systems
 */

import { IndustryConfig } from '../industryTypes'

export const pharmaceuticalsConfig: IndustryConfig = {
  id: 'pharmaceuticals',
  name: 'Pharmaceuticals',
  description: 'Pharmaceutical manufacturing equipment monitoring and GMP compliance',
  icon: 'Pill',

  terminology: {
    asset: 'Equipment',
    assets: 'Equipment',
    assetPlural: 'Equipment Units',
    assetType: 'Equipment Class',
    failure: 'Deviation',
    health: 'Qualification Status',
    healthScore: 'Qualification Score',
    healthy: 'Qualified',
    degraded: 'Under Review',
    critical: 'OOS (Out of Spec)',
    alert: 'Quality Alert',
    process: 'Manufacturing',
    maintenance: 'Qualified Maintenance',
    inspection: 'Validation',
    downtime: 'Production Downtime',
    workOrder: 'Maintenance Work Order',
    prediction: 'Forecast',
    anomaly: 'Deviation',
    trend: 'Trend',
    risk: 'Quality Risk',
    facility: 'Facility',
    facilityPlural: 'Facilities',
    location: 'Clean Room',
    operator: 'Manufacturing Operator',
    supervisor: 'Production Manager',
    calibration: 'GMP Calibration',
    compliance: 'GMP Compliance',
    criticality: 'Equipment Criticality',
  },

  assetTypes: [
    {
      id: 'bioreactor',
      name: 'Bioreactor',
      icon: 'FlaskConical',
      description: 'Biological production vessels for cell culture and fermentation',
      category: 'Bioprocessing',
      defaultMetrics: ['dissolved_oxygen', 'ph_level', 'temperature', 'agitation_speed', 'cell_density'],
      maintenanceTypes: ['sterilization', 'sensor_calibration', 'gasket_replacement', 'probe_maintenance'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Primary production bioreactor', downtimeCostMultiplier: 15 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Secondary production unit', downtimeCostMultiplier: 8 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Development scale unit', downtimeCostMultiplier: 4 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Backup or pilot unit', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'tablet_press',
      name: 'Tablet Press',
      icon: 'CircleDot',
      description: 'High-speed rotary tablet compression equipment',
      category: 'Solid Dosage',
      defaultMetrics: ['compression_force', 'tablet_weight', 'hardness', 'turret_speed', 'rejection_rate'],
      maintenanceTypes: ['tooling_inspection', 'turret_alignment', 'force_calibration', 'lubrication'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'High-volume production press', downtimeCostMultiplier: 12 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Standard production press', downtimeCostMultiplier: 6 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Development press', downtimeCostMultiplier: 3 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Backup press', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'coating_machine',
      name: 'Coating Machine',
      icon: 'Layers',
      description: 'Film coating equipment for tablet and capsule coating',
      category: 'Solid Dosage',
      defaultMetrics: ['inlet_temperature', 'exhaust_temperature', 'spray_rate', 'pan_speed', 'coating_uniformity'],
      maintenanceTypes: ['spray_gun_cleaning', 'air_system_service', 'pan_inspection', 'filter_replacement'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Primary coating system', downtimeCostMultiplier: 10 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Secondary coating unit', downtimeCostMultiplier: 5 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Development coater', downtimeCostMultiplier: 2 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Lab scale coater', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'filling_line',
      name: 'Filling Line',
      icon: 'Pipette',
      description: 'Aseptic filling equipment for vials, syringes, and cartridges',
      category: 'Sterile Manufacturing',
      defaultMetrics: ['fill_volume', 'fill_accuracy', 'stopper_placement', 'particle_count', 'line_speed'],
      maintenanceTypes: ['aseptic_validation', 'pump_calibration', 'isolator_integrity', 'filter_integrity_test'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Primary sterile filling line', downtimeCostMultiplier: 20 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Secondary filling line', downtimeCostMultiplier: 10 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Clinical trial filling', downtimeCostMultiplier: 5 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Development filling', downtimeCostMultiplier: 2 },
      ],
    },
    {
      id: 'autoclave',
      name: 'Autoclave',
      icon: 'Thermometer',
      description: 'Steam sterilization equipment for equipment and materials',
      category: 'Sterilization',
      defaultMetrics: ['chamber_temperature', 'chamber_pressure', 'cycle_time', 'f0_value', 'vacuum_level'],
      maintenanceTypes: ['gasket_replacement', 'valve_service', 'thermocouple_calibration', 'safety_system_test'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Production sterilization', downtimeCostMultiplier: 15 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Equipment sterilization', downtimeCostMultiplier: 8 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Laboratory sterilization', downtimeCostMultiplier: 3 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Backup autoclave', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'hvac_system',
      name: 'HVAC System',
      icon: 'Wind',
      description: 'Cleanroom air handling and environmental control systems',
      category: 'Facility Systems',
      defaultMetrics: ['temperature', 'relative_humidity', 'differential_pressure', 'air_changes_per_hour', 'particle_count'],
      maintenanceTypes: ['filter_replacement', 'damper_inspection', 'coil_cleaning', 'sensor_calibration'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Aseptic area HVAC', downtimeCostMultiplier: 25 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Controlled area HVAC', downtimeCostMultiplier: 12 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Support area HVAC', downtimeCostMultiplier: 4 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'General area HVAC', downtimeCostMultiplier: 1 },
      ],
    },
  ],

  kpiDomains: [
    {
      id: 'product_quality',
      name: 'Product Quality',
      icon: 'Award',
      description: 'Product quality metrics and batch release performance',
      businessValue: 'Ensure patient safety and regulatory compliance through quality metrics',
      category: 'industry_specific',
      metrics: [
        { id: 'batch_yield', name: 'Batch Yield', unit: '%', description: 'Percentage of acceptable product per batch', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 98, warning: 95, critical: 90 } },
        { id: 'right_first_time', name: 'Right First Time', unit: '%', description: 'Batches passing without rework', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 99, warning: 95, critical: 90 } },
        { id: 'oos_rate', name: 'OOS Rate', unit: 'ppm', description: 'Out of specification results per million', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 10, warning: 50, critical: 100 } },
        { id: 'deviation_rate', name: 'Deviation Rate', unit: 'per batch', description: 'Number of deviations per batch', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 0.1, warning: 0.5, critical: 1.0 } },
      ],
      thresholds: { healthy: { min: 98 }, warning: { min: 95, max: 98 }, critical: { max: 95 } },
      visualizations: ['bar_chart', 'trend', 'comparison', 'distribution'],
    },
    {
      id: 'gmp_compliance',
      name: 'GMP Compliance',
      icon: 'ShieldCheck',
      description: 'Good Manufacturing Practice compliance metrics',
      businessValue: 'Maintain regulatory compliance and avoid FDA warning letters',
      category: 'industry_specific',
      metrics: [
        { id: 'capa_closure_rate', name: 'CAPA Closure Rate', unit: '%', description: 'Percentage of CAPAs closed on time', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 95, warning: 85, critical: 75 } },
        { id: 'deviation_closure_time', name: 'Deviation Closure Time', unit: 'days', description: 'Average days to close deviations', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 30, warning: 45, critical: 60 } },
        { id: 'training_compliance', name: 'Training Compliance', unit: '%', description: 'Personnel with current training', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 95, critical: 90 } },
        { id: 'sop_adherence', name: 'SOP Adherence', unit: '%', description: 'Compliance with standard operating procedures', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 98, critical: 95 } },
      ],
      thresholds: { healthy: { min: 95 }, warning: { min: 85, max: 95 }, critical: { max: 85 } },
      visualizations: ['gauge', 'trend', 'bar_chart', 'comparison'],
    },
    {
      id: 'equipment_qualification',
      name: 'Equipment Qualification',
      icon: 'CheckCircle2',
      description: 'Equipment validation and qualification status',
      businessValue: 'Ensure equipment operates within validated parameters',
      category: 'industry_specific',
      metrics: [
        { id: 'qualification_status', name: 'Qualification Status', unit: '%', description: 'Percentage of equipment in qualified state', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 95, critical: 90 } },
        { id: 'calibration_compliance', name: 'Calibration Compliance', unit: '%', description: 'Instruments with current calibration', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 98, critical: 95 } },
        { id: 'validation_currency', name: 'Validation Currency', unit: '%', description: 'Systems with current validation', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 95, critical: 90 } },
        { id: 'pq_overdue', name: 'PQ Overdue', unit: 'count', description: 'Number of overdue performance qualifications', dataType: 'discrete', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 0, warning: 2, critical: 5 } },
      ],
      thresholds: { healthy: { min: 100 }, warning: { min: 95, max: 100 }, critical: { max: 95 } },
      visualizations: ['gauge', 'bar_chart', 'trend', 'heatmap'],
    },
    {
      id: 'environmental_monitoring',
      name: 'Environmental Monitoring',
      icon: 'Thermometer',
      description: 'Cleanroom environmental control and monitoring',
      businessValue: 'Maintain controlled environments for product integrity',
      category: 'industry_specific',
      metrics: [
        { id: 'viable_particle_count', name: 'Viable Particle Count', unit: 'CFU/m3', description: 'Microbial contamination level', dataType: 'continuous', aggregation: 'max', goodDirection: 'lower', defaultThresholds: { healthy: 1, warning: 5, critical: 10 } },
        { id: 'non_viable_particle_count', name: 'Non-Viable Particles', unit: 'particles/m3', description: 'Non-viable particle count at 0.5um', dataType: 'continuous', aggregation: 'max', goodDirection: 'lower', defaultThresholds: { healthy: 3520, warning: 35200, critical: 352000 } },
        { id: 'temperature_excursions', name: 'Temperature Excursions', unit: 'count', description: 'Number of temperature excursions', dataType: 'discrete', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 0, warning: 1, critical: 3 } },
        { id: 'humidity_excursions', name: 'Humidity Excursions', unit: 'count', description: 'Number of humidity excursions', dataType: 'discrete', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 0, warning: 1, critical: 3 } },
      ],
      thresholds: { healthy: { min: 0 }, warning: { min: 0 }, critical: { min: 0 } },
      visualizations: ['line_chart', 'heatmap', 'trend', 'distribution'],
    },
  ],

  maintenanceTypes: [
    {
      id: 'qualified_preventive',
      name: 'Qualified Preventive Maintenance',
      description: 'GMP-compliant scheduled preventive maintenance',
      category: 'preventive',
      defaultInterval: { value: 6, unit: 'months' },
      laborRequirements: [
        { role: 'Calibration Technician', count: 1, hourlyRate: 55 },
        { role: 'Quality Assurance', count: 1, hourlyRate: 65 },
      ],
      estimatedDuration: { min: 2, typical: 4, max: 8, unit: 'hours' },
      skillsRequired: ['gmp_training', 'equipment_specific', 'documentation'],
      partsCommonly: ['filters', 'gaskets', 'o_rings', 'lubricants'],
    },
    {
      id: 'corrective',
      name: 'Corrective Maintenance',
      description: 'Repair after deviation or failure with deviation documentation',
      category: 'corrective',
      laborRequirements: [
        { role: 'Maintenance Technician', count: 2, hourlyRate: 50 },
        { role: 'Quality Assurance', count: 1, hourlyRate: 65 },
        { role: 'Validation Engineer', count: 1, hourlyRate: 85 },
      ],
      estimatedDuration: { min: 4, typical: 12, max: 48, unit: 'hours' },
      skillsRequired: ['troubleshooting', 'gmp_training', 'deviation_handling', 'change_control'],
      partsCommonly: ['sensors', 'motors', 'pumps', 'valves', 'seals'],
    },
    {
      id: 'revalidation',
      name: 'Revalidation',
      description: 'Equipment performance requalification after changes or repairs',
      category: 'condition_based',
      laborRequirements: [
        { role: 'Validation Engineer', count: 1, hourlyRate: 85 },
        { role: 'Quality Assurance', count: 1, hourlyRate: 65 },
        { role: 'Production Specialist', count: 1, hourlyRate: 55 },
      ],
      estimatedDuration: { min: 8, typical: 24, max: 72, unit: 'hours' },
      skillsRequired: ['validation_protocols', 'statistical_analysis', 'documentation'],
      partsCommonly: ['calibration_standards', 'reference_materials'],
    },
    {
      id: 'calibration',
      name: 'Calibration',
      description: 'GMP-compliant instrument calibration with traceability',
      category: 'preventive',
      defaultInterval: { value: 12, unit: 'months' },
      laborRequirements: [
        { role: 'Calibration Technician', count: 1, hourlyRate: 55 },
      ],
      estimatedDuration: { min: 30, typical: 90, max: 240, unit: 'minutes' },
      skillsRequired: ['metrology', 'gmp_documentation', 'instrument_specific'],
      partsCommonly: ['calibration_standards', 'reference_instruments'],
    },
  ],

  riskFactors: [
    { id: 'product_quality', name: 'Product Quality Risk', description: 'Risk of quality failures affecting patient safety', category: 'safety', weight: 0.35, calculationMethod: 'exponential', inputs: ['oos_rate', 'deviation_rate', 'batch_yield'] },
    { id: 'regulatory', name: 'Regulatory Risk', description: 'Risk of regulatory non-compliance and enforcement actions', category: 'compliance', weight: 0.30, calculationMethod: 'threshold', inputs: ['capa_closure_rate', 'training_compliance', 'validation_currency'] },
    { id: 'contamination', name: 'Contamination Risk', description: 'Risk of microbial or particulate contamination', category: 'safety', weight: 0.20, calculationMethod: 'exponential', inputs: ['viable_particle_count', 'non_viable_particle_count', 'environmental_excursions'] },
    { id: 'supply_chain', name: 'Supply Chain Risk', description: 'Risk of production delays affecting drug supply', category: 'operational', weight: 0.15, calculationMethod: 'linear', inputs: ['equipment_availability', 'spare_parts_inventory', 'vendor_performance'] },
  ],

  complianceStandards: [
    {
      id: 'fda_21_cfr_211',
      name: 'FDA 21 CFR Part 211',
      shortName: 'cGMP',
      description: 'Current Good Manufacturing Practice for Finished Pharmaceuticals',
      authority: 'US FDA',
      region: ['US'],
      requirements: [
        { id: 'equipment_qualification', name: 'Equipment Qualification', description: 'All equipment must be qualified for intended use', kpiId: 'qualification_status', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'calibration_status', name: 'Calibration Current', description: 'All measuring instruments must be calibrated', kpiId: 'calibration_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'maintenance_records', name: 'Maintenance Records', description: 'Complete maintenance documentation required', kpiId: 'documentation_complete', threshold: 100, operator: 'eq', severity: 'required' },
        { id: 'training_current', name: 'Training Current', description: 'Personnel must have current GMP training', kpiId: 'training_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
      ],
      reportingFrequency: 'monthly',
    },
    {
      id: 'eu_gmp_annex_15',
      name: 'EU GMP Annex 15',
      shortName: 'EU Annex 15',
      description: 'Qualification and Validation requirements for EU GMP',
      authority: 'European Medicines Agency',
      region: ['EU', 'UK'],
      requirements: [
        { id: 'validation_master_plan', name: 'Validation Master Plan', description: 'VMP must be current and approved', kpiId: 'vmp_status', threshold: 100, operator: 'eq', severity: 'required' },
        { id: 'iq_oq_pq', name: 'IQ/OQ/PQ Complete', description: 'All qualifications must be completed and current', kpiId: 'qualification_status', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'change_control', name: 'Change Control', description: 'All changes must go through change control', kpiId: 'change_control_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
      ],
      reportingFrequency: 'quarterly',
    },
    {
      id: 'ich_q10',
      name: 'ICH Q10',
      shortName: 'ICH Q10',
      description: 'Pharmaceutical Quality System guidelines',
      authority: 'International Council for Harmonisation',
      region: ['Global'],
      requirements: [
        { id: 'capa_effectiveness', name: 'CAPA Effectiveness', description: 'CAPAs must be effective and timely', kpiId: 'capa_closure_rate', threshold: 95, operator: 'gte', severity: 'required' },
        { id: 'process_performance', name: 'Process Performance', description: 'Processes must perform within validated ranges', kpiId: 'process_capability', threshold: 1.33, operator: 'gte', severity: 'required' },
        { id: 'knowledge_management', name: 'Knowledge Management', description: 'Product and process knowledge documented', kpiId: 'knowledge_capture', threshold: 100, operator: 'eq', severity: 'advisory' },
      ],
      reportingFrequency: 'quarterly',
    },
    {
      id: 'who_gmp',
      name: 'WHO GMP',
      shortName: 'WHO GMP',
      description: 'World Health Organization Good Manufacturing Practices',
      authority: 'World Health Organization',
      region: ['Global'],
      requirements: [
        { id: 'qualified_personnel', name: 'Qualified Personnel', description: 'Personnel must be qualified for their roles', kpiId: 'training_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'environmental_control', name: 'Environmental Control', description: 'Manufacturing areas must meet environmental specifications', kpiId: 'environmental_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'documentation_practices', name: 'Documentation Practices', description: 'All activities must be documented', kpiId: 'documentation_complete', threshold: 100, operator: 'eq', severity: 'critical' },
      ],
      reportingFrequency: 'monthly',
    },
  ],

  visualization: {
    primaryColor: '#0ea5e9',
    secondaryColor: '#f0f9ff',
    healthyColor: '#10b981',
    warningColor: '#f59e0b',
    criticalColor: '#ef4444',
    chartDefaults: {
      showGrid: true,
      showLegend: true,
      animationDuration: 300,
    },
  },

  narrativeTemplates: [
    {
      id: 'health_summary',
      name: 'Health Summary',
      context: 'asset_health',
      audience: ['executive', 'manager', 'engineer'],
      template: 'Your {assetType} "{assetName}" is currently {healthStatus}. {trendDescription} Over the past {timeRange}, {kpiSummary}. {recommendationText}',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'healthStatus', type: 'terminology' },
        { name: 'trendDescription', type: 'metric' },
        { name: 'timeRange', type: 'time' },
        { name: 'kpiSummary', type: 'metric' },
        { name: 'recommendationText', type: 'metric' },
      ],
    },
    {
      id: 'prediction_alert',
      name: 'Prediction Alert',
      context: 'prediction',
      audience: ['manager', 'engineer', 'technician'],
      template: 'AI {prediction} for {assetType} "{assetName}": {predictionDetails}. Confidence: {confidence}%. Based on analysis of {dataPoints} data points. Recommended action: {recommendation}',
      variables: [
        { name: 'prediction', type: 'terminology' },
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'predictionDetails', type: 'metric' },
        { name: 'confidence', type: 'percentage' },
        { name: 'dataPoints', type: 'metric' },
        { name: 'recommendation', type: 'metric' },
      ],
    },
    {
      id: 'deviation_report',
      name: 'Deviation Report',
      context: 'anomaly',
      audience: ['manager', 'engineer'],
      template: 'A {anomaly} has been detected on {assetType} "{assetName}" in {location}. {deviationDescription} This may impact {impactDescription}. Immediate action: {immediateAction}',
      variables: [
        { name: 'anomaly', type: 'terminology' },
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'location', type: 'terminology' },
        { name: 'deviationDescription', type: 'metric' },
        { name: 'impactDescription', type: 'metric' },
        { name: 'immediateAction', type: 'metric' },
      ],
    },
    {
      id: 'qualification_status',
      name: 'Qualification Status',
      context: 'maintenance',
      audience: ['manager', 'engineer'],
      template: '{assetType} "{assetName}" qualification status: {healthStatus}. Last validation: {lastValidation}. Next scheduled requalification: {nextRequalification}. {complianceNote}',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'healthStatus', type: 'terminology' },
        { name: 'lastValidation', type: 'time' },
        { name: 'nextRequalification', type: 'time' },
        { name: 'complianceNote', type: 'metric' },
      ],
    },
  ],

  features: {
    enableBenchmarking: true,
    enableWhatIf: true,
    enableCollaboration: true,
    enableCompliance: true,
    enableKnowledgeCapture: true,
  },

  defaults: {
    timeRange: '24h',
    refreshInterval: 15,
    defaultDashboard: 'command-center',
  },

  // =============================================================================
  // PROFESSIONAL ROLES (Pharmaceuticals-Specific)
  // =============================================================================
  professionalRoles: [
    // SME Roles
    {
      id: 'validation_engineer',
      category: 'sme',
      name: 'Validation Engineer',
      shortName: 'VE',
      description: 'Develops and executes validation protocols to ensure GMP compliance and product quality',
      responsibilities: [
        'Equipment qualification (IQ/OQ/PQ) development and execution',
        'Process validation protocol design',
        'Cleaning validation studies',
        'Computer system validation (CSV)',
        'Deviation investigation and CAPA development',
      ],
      focusAreas: ['qualification_status', 'validation_currency', 'process_capability', 'deviation_rate', 'capa_effectiveness'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'compliance', 'action_items'],
      },
      decisionAuthority: ['validation_protocols', 'qualification_acceptance', 'deviation_classification'],
      reportingTo: 'Quality Director',
      icon: 'ClipboardCheck',
    },
    // Technician Roles
    {
      id: 'calibration_technician',
      category: 'technician',
      name: 'Calibration Technician',
      shortName: 'CT',
      description: 'Performs GMP-compliant calibration of instruments and equipment',
      responsibilities: [
        'Instrument calibration per SOPs',
        'Calibration record maintenance',
        'Out-of-tolerance investigation',
        'Reference standard management',
        'Calibration certificate preparation',
      ],
      focusAreas: ['calibration_compliance', 'instrument_accuracy', 'tolerance_status', 'calibration_schedule'],
      explanationStyle: {
        depth: 'technical',
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'action_items', 'compliance'],
      },
      decisionAuthority: ['calibration_methods', 'tolerance_acceptance', 'instrument_adjustment'],
      reportingTo: 'Validation Engineer',
      icon: 'Ruler',
    },
    // Executive Roles
    {
      id: 'quality_director',
      category: 'executive',
      name: 'Quality Director',
      shortName: 'QD',
      description: 'Oversees quality systems, regulatory compliance, and batch release decisions',
      responsibilities: [
        'Quality system oversight',
        'Regulatory inspection management',
        'Batch release authorization',
        'CAPA program management',
        'Quality metrics and trending',
      ],
      focusAreas: ['batch_yield', 'oos_rate', 'capa_closure_rate', 'compliance_score', 'audit_findings'],
      explanationStyle: {
        depth: 'high_level',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'executive',
        focusOn: ['business_impact', 'compliance', 'risk'],
      },
      decisionAuthority: ['batch_release', 'deviation_escalation', 'audit_response', 'quality_policy'],
      reportingTo: 'VP Quality',
      icon: 'Shield',
    },
    // Analyst Roles
    {
      id: 'process_analyst',
      category: 'analyst',
      name: 'Process Analyst',
      shortName: 'PA',
      description: 'Analyzes process data to optimize manufacturing and ensure quality',
      responsibilities: [
        'Process parameter trending',
        'Statistical process control',
        'Batch record review',
        'Process improvement initiatives',
        'Technology transfer support',
      ],
      focusAreas: ['process_capability', 'batch_yield', 'cycle_time', 'variation_analysis', 'trending'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['technical_details', 'business_impact'],
      },
      decisionAuthority: ['process_parameters', 'control_limits', 'data_analysis_methods'],
      reportingTo: 'Production Manager',
      icon: 'LineChart',
    },
  ],

  // =============================================================================
  // PARTS CATALOG (Pharmaceutical Equipment)
  // =============================================================================
  partsCatalog: {
    categories: [
      {
        id: 'critical_process',
        name: 'Critical Process Components',
        description: 'Components directly impacting product quality',
        parts: [
          {
            id: 'bioreactor_sparger',
            partNumber: 'SPG-SS316-10L',
            name: 'Bioreactor Sparger Assembly',
            description: '316L stainless steel sparger for 10L bioreactor, 2um pore size',
            category: 'critical_process',
            manufacturer: 'Sartorius',
            unitCost: 850.00,
            currency: 'USD',
            leadTime: { value: 14, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['bioreactor'],
            failureModes: ['sparger_blockage', 'contamination', 'flow_restriction'],
            averageLifespan: { value: 2, unit: 'years' },
            specifications: { material: '316L SS', poreSize: '2um', volume: '10L' },
          },
          {
            id: 'tablet_tooling_set',
            partNumber: 'TOOL-RND-10MM',
            name: 'Tablet Tooling Set - Round 10mm',
            description: 'Upper and lower punch set with die for 10mm round tablets',
            category: 'critical_process',
            manufacturer: 'Natoli',
            unitCost: 2200.00,
            currency: 'USD',
            leadTime: { value: 21, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['tablet_press'],
            failureModes: ['tooling_wear', 'picking_sticking', 'capping'],
            averageLifespan: { value: 500000, unit: 'cycles' },
            specifications: { diameter: '10mm', shape: 'round', material: 'D2 steel' },
          },
          {
            id: 'filling_pump_assembly',
            partNumber: 'PUMP-PERI-100ML',
            name: 'Peristaltic Pump Assembly',
            description: 'Precision peristaltic pump for sterile filling, 0.1-100mL range',
            category: 'critical_process',
            manufacturer: 'Watson-Marlow',
            unitCost: 3500.00,
            currency: 'USD',
            leadTime: { value: 28, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['filling_line'],
            failureModes: ['pump_failure', 'fill_accuracy_drift', 'tubing_wear'],
            averageLifespan: { value: 5000, unit: 'hours' },
            specifications: { range: '0.1-100mL', accuracy: '±0.5%', material: 'FDA-approved' },
          },
          {
            id: 'autoclave_door_gasket',
            partNumber: 'GASK-SIL-DOOR',
            name: 'Autoclave Door Gasket',
            description: 'Silicone door gasket for steam sterilizer, FDA-compliant',
            category: 'critical_process',
            manufacturer: 'Steris',
            unitCost: 450.00,
            currency: 'USD',
            leadTime: { value: 7, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['autoclave'],
            failureModes: ['seal_leak', 'gasket_degradation', 'vacuum_loss'],
            averageLifespan: { value: 1, unit: 'years' },
            specifications: { material: 'Silicone', rating: 'FDA 21 CFR 177.2600' },
          },
        ],
      },
      {
        id: 'instrumentation',
        name: 'Instrumentation & Sensors',
        description: 'Measurement and control instrumentation',
        parts: [
          {
            id: 'ph_probe',
            partNumber: 'PH-STEAM-INLINE',
            name: 'Steam Sterilizable pH Probe',
            description: 'In-line pH probe for bioreactor, steam sterilizable',
            category: 'instrumentation',
            manufacturer: 'Hamilton',
            unitCost: 1200.00,
            currency: 'USD',
            leadTime: { value: 10, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['bioreactor'],
            failureModes: ['drift', 'membrane_damage', 'reference_depletion'],
            averageLifespan: { value: 12, unit: 'months' },
            specifications: { range: '0-14 pH', accuracy: '±0.02 pH', sterilizable: 'Yes' },
          },
          {
            id: 'do_sensor',
            partNumber: 'DO-OPT-INLINE',
            name: 'Optical DO Sensor',
            description: 'Optical dissolved oxygen sensor for bioprocess monitoring',
            category: 'instrumentation',
            manufacturer: 'Mettler Toledo',
            unitCost: 2800.00,
            currency: 'USD',
            leadTime: { value: 14, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['bioreactor'],
            failureModes: ['membrane_degradation', 'calibration_drift', 'response_time_increase'],
            averageLifespan: { value: 18, unit: 'months' },
            specifications: { range: '0-100%', accuracy: '±1%', response: '<60s' },
          },
          {
            id: 'pressure_transmitter',
            partNumber: 'PT-SANI-0-100PSI',
            name: 'Sanitary Pressure Transmitter',
            description: '3A sanitary pressure transmitter, 0-100 PSI, 4-20mA output',
            category: 'instrumentation',
            manufacturer: 'Endress+Hauser',
            unitCost: 1800.00,
            currency: 'USD',
            leadTime: { value: 7, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['bioreactor', 'filling_line', 'autoclave'],
            failureModes: ['diaphragm_damage', 'calibration_drift', 'zero_shift'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { range: '0-100 PSI', output: '4-20mA', certification: '3A' },
          },
          {
            id: 'particle_counter',
            partNumber: 'PC-LASER-0.5UM',
            name: 'Laser Particle Counter',
            description: 'Laser-based airborne particle counter, 0.5-25um range',
            category: 'instrumentation',
            manufacturer: 'TSI',
            unitCost: 8500.00,
            currency: 'USD',
            leadTime: { value: 21, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['hvac_system', 'filling_line'],
            failureModes: ['laser_degradation', 'pump_failure', 'calibration_drift'],
            averageLifespan: { value: 7, unit: 'years' },
            specifications: { range: '0.5-25um', flowRate: '1 CFM', channels: '6' },
          },
        ],
      },
      {
        id: 'cleanroom',
        name: 'Cleanroom Components',
        description: 'Components for cleanroom and environmental control',
        parts: [
          {
            id: 'hepa_filter',
            partNumber: 'HEPA-24x24-H14',
            name: 'HEPA Filter 24x24 H14',
            description: 'H14 HEPA filter, 24x24x6 inches, 99.995% efficiency',
            category: 'cleanroom',
            manufacturer: 'Camfil',
            unitCost: 650.00,
            currency: 'USD',
            leadTime: { value: 5, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['hvac_system'],
            failureModes: ['filter_loading', 'media_damage', 'seal_leak'],
            averageLifespan: { value: 3, unit: 'years' },
            specifications: { size: '24x24x6 in', class: 'H14', efficiency: '99.995%' },
          },
          {
            id: 'ulpa_filter',
            partNumber: 'ULPA-24x24-U15',
            name: 'ULPA Filter 24x24 U15',
            description: 'U15 ULPA filter for Grade A/B areas, 99.9995% efficiency',
            category: 'cleanroom',
            manufacturer: 'AAF',
            unitCost: 1200.00,
            currency: 'USD',
            leadTime: { value: 10, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['hvac_system', 'filling_line'],
            failureModes: ['filter_loading', 'media_damage', 'seal_leak'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { size: '24x24x12 in', class: 'U15', efficiency: '99.9995%' },
          },
          {
            id: 'airlock_door_seal',
            partNumber: 'SEAL-AIRLOCK-INFL',
            name: 'Inflatable Door Seal',
            description: 'Inflatable door seal for cleanroom airlock, FDA-compliant',
            category: 'cleanroom',
            manufacturer: 'Rite-Hite',
            unitCost: 350.00,
            currency: 'USD',
            leadTime: { value: 7, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['hvac_system'],
            failureModes: ['seal_wear', 'inflation_failure', 'pressure_loss'],
            averageLifespan: { value: 2, unit: 'years' },
            specifications: { material: 'Silicone', pressure: '15-30 PSI' },
          },
        ],
      },
      {
        id: 'packaging',
        name: 'Packaging Components',
        description: 'Components for pharmaceutical packaging equipment',
        parts: [
          {
            id: 'vial_stopper',
            partNumber: 'STOP-RUBBER-13MM',
            name: 'Rubber Stoppers 13mm',
            description: 'Butyl rubber stoppers for 13mm vial neck, pre-sterilized, 1000/bag',
            category: 'packaging',
            manufacturer: 'West Pharma',
            unitCost: 180.00,
            currency: 'USD',
            leadTime: { value: 14, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['filling_line'],
            failureModes: ['coring', 'fragmentation', 'seal_integrity'],
            specifications: { diameter: '13mm', material: 'Butyl rubber', quantity: '1000' },
          },
          {
            id: 'crimp_cap',
            partNumber: 'CRIMP-ALUM-13MM',
            name: 'Aluminum Crimp Caps 13mm',
            description: 'Aluminum flip-off crimp caps, 13mm, color-coded, 1000/bag',
            category: 'packaging',
            manufacturer: 'West Pharma',
            unitCost: 95.00,
            currency: 'USD',
            leadTime: { value: 7, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['filling_line'],
            failureModes: ['crimp_defect', 'seal_failure'],
            specifications: { diameter: '13mm', material: 'Aluminum', quantity: '1000' },
          },
          {
            id: 'blister_film',
            partNumber: 'FILM-PVC-PVDC',
            name: 'PVC/PVDC Blister Film',
            description: 'PVC/PVDC barrier film for blister packaging, 250mm width',
            category: 'packaging',
            manufacturer: 'Klockner Pentaplast',
            unitCost: 420.00,
            currency: 'USD',
            leadTime: { value: 10, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['coating_machine'],
            failureModes: ['seal_failure', 'moisture_ingress', 'film_break'],
            specifications: { width: '250mm', material: 'PVC/PVDC', thickness: '250um' },
          },
        ],
      },
    ],
  },

  // =============================================================================
  // ROOT CAUSE INTELLIGENCE (Pharmaceuticals)
  // =============================================================================
  rootCauseLibrary: {
    categories: [
      {
        id: 'contamination',
        name: 'Contamination',
        description: 'Root causes related to microbial, particulate, or cross-contamination',
        icon: 'Bug',
        color: '#ef4444',
        rootCauses: [
          {
            id: 'microbial_contamination',
            name: 'Microbial Contamination',
            description: 'Introduction of microorganisms into product or environment',
            category: 'contamination',
            symptoms: [
              'Elevated bioburden counts',
              'Environmental monitoring excursions',
              'Positive sterility test results',
              'Visible growth in media fills',
              'Increased endotoxin levels',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review environmental monitoring data for trends',
                expectedResult: 'Identify location and timing of contamination events',
                tools: ['LIMS', 'Trending software'],
                duration: 60,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Perform surface sampling in suspected areas',
                expectedResult: 'Identify contamination source location',
                tools: ['Contact plates', 'Swabs', 'Settle plates'],
                duration: 120,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Identify organism to species level',
                expectedResult: 'Determine contamination source (environmental, human, water)',
                tools: ['Microbial identification system'],
                duration: 240,
                skillLevel: 'advanced',
              },
              {
                order: 4,
                action: 'Evaluate personnel gowning and behavior',
                expectedResult: 'Identify procedural gaps',
                tools: ['Video review', 'Observation'],
                duration: 60,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Implement robust environmental monitoring program',
              'Conduct regular gowning qualifications',
              'Maintain positive pressure differentials',
              'Ensure HEPA filter integrity',
              'Validate cleaning and disinfection procedures',
            ],
            typicalProbability: 0.15,
            affectedComponents: ['filling_nozzles', 'stopper_bowls', 'isolator_gloves', 'filter_housings'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 24, typical: 72, max: 168, unit: 'hours' },
            costRange: { min: 10000, max: 500000, currency: 'USD' },
            industryReferences: ['FDA Guidance on Sterile Drug Products', 'EU GMP Annex 1'],
          },
          {
            id: 'cross_contamination',
            name: 'Cross-Contamination',
            description: 'Transfer of material from one product to another',
            category: 'contamination',
            symptoms: [
              'Unexpected API in product',
              'Failed identity tests',
              'Residue detection in rinse samples',
              'Product mix-ups',
              'Label mix-ups',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review cleaning validation data',
                expectedResult: 'Verify cleaning procedures are effective',
                tools: ['Analytical methods', 'HPLC'],
                duration: 120,
                skillLevel: 'advanced',
              },
              {
                order: 2,
                action: 'Evaluate campaign sequencing',
                expectedResult: 'Identify high-risk product transitions',
                tools: ['Production schedule', 'Risk assessment'],
                duration: 60,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Perform equipment swab analysis',
                expectedResult: 'Detect residual contamination',
                tools: ['Swab kits', 'TOC analyzer', 'HPLC'],
                duration: 180,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Implement dedicated equipment where possible',
              'Validate cleaning procedures with worst-case products',
              'Use visual inspection with UV light',
              'Implement equipment status labeling',
              'Conduct swab sampling verification',
            ],
            typicalProbability: 0.10,
            affectedComponents: ['vessels', 'piping', 'filters', 'process_equipment'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 8, typical: 24, max: 72, unit: 'hours' },
            costRange: { min: 5000, max: 100000, currency: 'USD' },
            industryReferences: ['FDA Guidance on Equipment Cleaning', 'PDA TR29'],
          },
        ],
      },
      {
        id: 'calibration_drift',
        name: 'Calibration Drift',
        description: 'Root causes related to instrument and measurement accuracy',
        icon: 'Ruler',
        color: '#f59e0b',
        rootCauses: [
          {
            id: 'sensor_drift',
            name: 'Sensor Drift',
            description: 'Gradual change in sensor accuracy over time',
            category: 'calibration_drift',
            symptoms: [
              'Out-of-tolerance calibration results',
              'Process parameter trending outside limits',
              'Product quality variations',
              'Batch rejection due to parameter excursions',
              'Inconsistent readings between redundant sensors',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review calibration history trending',
                expectedResult: 'Identify drift pattern and rate',
                tools: ['Calibration database', 'Trending tools'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Compare to reference standard',
                expectedResult: 'Quantify current deviation',
                tools: ['Reference standards', 'Calibration equipment'],
                duration: 60,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Evaluate environmental factors',
                expectedResult: 'Identify temperature/humidity/vibration effects',
                tools: ['Environmental monitoring data'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 4,
                action: 'Assess sensor age and usage',
                expectedResult: 'Determine if replacement is needed',
                tools: ['Asset records', 'Manufacturer specifications'],
                duration: 15,
                skillLevel: 'basic',
              },
            ],
            preventiveActions: [
              'Implement risk-based calibration intervals',
              'Use trending to predict calibration needs',
              'Maintain environmental controls',
              'Replace sensors based on usage limits',
              'Implement redundant measurements for critical parameters',
            ],
            typicalProbability: 0.20,
            affectedComponents: ['ph_probes', 'temperature_sensors', 'pressure_transmitters', 'flow_meters'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 1, typical: 2, max: 4, unit: 'hours' },
            costRange: { min: 500, max: 5000, currency: 'USD' },
            industryReferences: ['FDA 21 CFR 211.68', 'USP <1058>'],
          },
          {
            id: 'reference_standard_degradation',
            name: 'Reference Standard Degradation',
            description: 'Degradation of calibration reference standards affecting accuracy',
            category: 'calibration_drift',
            symptoms: [
              'Systematic calibration shifts',
              'Multiple instruments showing similar drift',
              'Failed proficiency testing',
              'Inconsistent results between laboratories',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Verify reference standard certificate validity',
                expectedResult: 'Confirm standards are within certification period',
                tools: ['Certificate database'],
                duration: 15,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Check storage conditions',
                expectedResult: 'Verify proper storage per manufacturer requirements',
                tools: ['Environmental monitoring records'],
                duration: 20,
                skillLevel: 'basic',
              },
              {
                order: 3,
                action: 'Compare to independent reference',
                expectedResult: 'Verify standard accuracy',
                tools: ['External calibration service'],
                duration: 240,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Implement robust standard management program',
              'Track standard usage and age',
              'Maintain proper storage conditions',
              'Use certified reference materials',
              'Participate in proficiency testing programs',
            ],
            typicalProbability: 0.08,
            affectedComponents: ['calibration_weights', 'reference_thermometers', 'standard_solutions', 'reference_gauges'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 2, typical: 8, max: 24, unit: 'hours' },
            costRange: { min: 1000, max: 10000, currency: 'USD' },
            industryReferences: ['ISO 17025', 'USP <1058>'],
          },
        ],
      },
      {
        id: 'process_deviation',
        name: 'Process Deviation',
        description: 'Root causes related to manufacturing process parameters',
        icon: 'AlertTriangle',
        color: '#3b82f6',
        rootCauses: [
          {
            id: 'parameter_excursion',
            name: 'Process Parameter Excursion',
            description: 'Critical process parameter outside validated range',
            category: 'process_deviation',
            symptoms: [
              'Alarm activation for process parameter',
              'Batch record parameter outside specification',
              'Product attribute out of specification',
              'Unusual equipment behavior',
              'Operator observation of abnormal conditions',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Capture process data historian records',
                expectedResult: 'Document exact parameter values and duration',
                tools: ['Process historian', 'SCADA system'],
                duration: 15,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Review equipment performance data',
                expectedResult: 'Identify equipment-related causes',
                tools: ['Equipment logs', 'Maintenance records'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Evaluate environmental conditions',
                expectedResult: 'Rule out environmental factors',
                tools: ['Environmental monitoring data'],
                duration: 20,
                skillLevel: 'basic',
              },
              {
                order: 4,
                action: 'Interview operators',
                expectedResult: 'Gather observations and actions taken',
                tools: ['Interview forms'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 5,
                action: 'Assess product impact',
                expectedResult: 'Determine batch disposition',
                tools: ['Product specifications', 'Quality assessment'],
                duration: 60,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Implement PAT (Process Analytical Technology)',
              'Use statistical process control',
              'Establish pre-alarm thresholds',
              'Conduct regular process reviews',
              'Validate equipment for worst-case conditions',
            ],
            typicalProbability: 0.18,
            affectedComponents: ['control_systems', 'sensors', 'actuators', 'process_equipment'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 4, typical: 16, max: 72, unit: 'hours' },
            costRange: { min: 5000, max: 250000, currency: 'USD' },
            industryReferences: ['ICH Q8', 'ICH Q9', 'FDA Process Validation Guidance'],
          },
          {
            id: 'equipment_malfunction',
            name: 'Equipment Malfunction',
            description: 'Equipment failure affecting process control or product quality',
            category: 'process_deviation',
            symptoms: [
              'Equipment alarm or fault code',
              'Unexpected equipment shutdown',
              'Process parameter instability',
              'Unusual sounds or vibrations',
              'Visual equipment damage',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review equipment alarm logs',
                expectedResult: 'Identify fault codes and sequence',
                tools: ['HMI/PLC logs'],
                duration: 15,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Perform visual inspection',
                expectedResult: 'Identify visible damage or wear',
                tools: ['Inspection checklist', 'Flashlight'],
                duration: 30,
                skillLevel: 'basic',
              },
              {
                order: 3,
                action: 'Check maintenance history',
                expectedResult: 'Identify patterns or missed maintenance',
                tools: ['CMMS'],
                duration: 20,
                skillLevel: 'basic',
              },
              {
                order: 4,
                action: 'Test equipment functionality',
                expectedResult: 'Verify operation against specifications',
                tools: ['Test procedures', 'Calibration equipment'],
                duration: 60,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Implement predictive maintenance',
              'Conduct regular equipment qualification',
              'Maintain spare parts inventory',
              'Train maintenance personnel',
              'Document equipment history',
            ],
            typicalProbability: 0.15,
            affectedComponents: ['pumps', 'valves', 'motors', 'control_systems', 'sensors'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 2, typical: 8, max: 48, unit: 'hours' },
            costRange: { min: 1000, max: 100000, currency: 'USD' },
            industryReferences: ['FDA 21 CFR 211.67', 'EU GMP Annex 15'],
          },
        ],
      },
      {
        id: 'environmental',
        name: 'Environmental',
        description: 'Root causes related to cleanroom and environmental control',
        icon: 'Thermometer',
        color: '#10b981',
        rootCauses: [
          {
            id: 'hvac_failure',
            name: 'HVAC System Failure',
            description: 'Failure of environmental control systems affecting cleanroom conditions',
            category: 'environmental',
            symptoms: [
              'Temperature excursion alarms',
              'Humidity excursion alarms',
              'Differential pressure loss',
              'Increased particle counts',
              'Comfort complaints from personnel',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Check BMS alarms and status',
                expectedResult: 'Identify system component in fault',
                tools: ['Building Management System'],
                duration: 10,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Verify AHU operation',
                expectedResult: 'Confirm fan, heating, and cooling status',
                tools: ['AHU inspection', 'Control panel'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Check filter differential pressure',
                expectedResult: 'Verify filters are not loaded',
                tools: ['Pressure gauges', 'Filter inspection'],
                duration: 20,
                skillLevel: 'basic',
              },
              {
                order: 4,
                action: 'Verify damper positions',
                expectedResult: 'Confirm proper airflow control',
                tools: ['BMS', 'Visual inspection'],
                duration: 15,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Implement predictive maintenance for HVAC',
              'Monitor filter loading trends',
              'Maintain redundant systems',
              'Conduct regular BMS calibration',
              'Perform annual rebalancing',
            ],
            typicalProbability: 0.12,
            affectedComponents: ['ahu_fans', 'cooling_coils', 'hepa_filters', 'dampers', 'humidifiers'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 1, typical: 4, max: 24, unit: 'hours' },
            costRange: { min: 2000, max: 50000, currency: 'USD' },
            industryReferences: ['ISO 14644', 'EU GMP Annex 1'],
          },
          {
            id: 'water_system_excursion',
            name: 'Water System Excursion',
            description: 'Purified or WFI water system out of specification',
            category: 'environmental',
            symptoms: [
              'TOC exceeding alert/action limits',
              'Conductivity out of specification',
              'Bioburden exceeding limits',
              'Endotoxin detection',
              'Visual contamination',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review water monitoring trends',
                expectedResult: 'Identify when excursion began',
                tools: ['LIMS', 'Water monitoring system'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Sample at multiple points of use',
                expectedResult: 'Localize contamination source',
                tools: ['Sampling equipment', 'TOC analyzer'],
                duration: 120,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Check UV lamp intensity',
                expectedResult: 'Verify adequate UV treatment',
                tools: ['UV intensity meter'],
                duration: 15,
                skillLevel: 'basic',
              },
              {
                order: 4,
                action: 'Inspect RO membranes',
                expectedResult: 'Check membrane integrity',
                tools: ['Conductivity meter', 'Membrane testing'],
                duration: 60,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Maintain continuous water monitoring',
              'Implement regular sanitization cycles',
              'Replace UV lamps per manufacturer schedule',
              'Conduct periodic membrane integrity testing',
              'Maintain adequate loop velocity',
            ],
            typicalProbability: 0.10,
            affectedComponents: ['ro_membranes', 'uv_lamps', 'distribution_loop', 'storage_tanks', 'vent_filters'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 4, typical: 24, max: 72, unit: 'hours' },
            costRange: { min: 5000, max: 75000, currency: 'USD' },
            industryReferences: ['USP <1231>', 'FDA Guidance on CGMP Water Systems'],
          },
        ],
      },
    ],
    commonPatterns: [
      {
        id: 'contamination_cascade',
        name: 'Contamination Event Cascade',
        description: 'Progression from initial contamination to widespread quality impact',
        sequence: [
          'Initial contamination introduction',
          'Environmental monitoring excursion',
          'Product bioburden increase',
          'Batch rejection or recall',
          'Regulatory inspection finding',
          'Production shutdown',
        ],
        indicators: [
          'Single environmental excursion',
          'Unusual organism identification',
          'Increase in alert level results',
        ],
        preventionStrategy: 'Implement robust environmental monitoring with trending and rapid response protocols',
      },
      {
        id: 'calibration_impact',
        name: 'Calibration Failure Impact Chain',
        description: 'Progression from instrument drift to product quality impact',
        sequence: [
          'Instrument calibration drift begins',
          'Process parameters affected',
          'Product attributes impacted',
          'Out-of-specification results',
          'Deviation investigation required',
          'Potential batch rejection',
        ],
        indicators: [
          'Trending calibration adjustments',
          'Process parameter variability increase',
          'Product attribute trending',
        ],
        preventionStrategy: 'Implement risk-based calibration with trending analysis and proactive replacement',
      },
      {
        id: 'environmental_cascade',
        name: 'Environmental Control Cascade',
        description: 'Progression from HVAC issue to cleanroom classification loss',
        sequence: [
          'HVAC component degradation',
          'Differential pressure reduction',
          'Air change rate decrease',
          'Particle count increase',
          'Cleanroom classification excursion',
          'Production area shutdown',
        ],
        indicators: [
          'Filter pressure drop trending high',
          'Differential pressure trending low',
          'Fan vibration increasing',
        ],
        preventionStrategy: 'Monitor HVAC performance indicators and implement predictive maintenance',
      },
    ],
  },

  // =============================================================================
  // LABOR RATES (Pharmaceutical Industry)
  // =============================================================================
  laborRates: {
    roles: [
      { roleId: 'calibration_technician', name: 'Calibration Technician', hourlyRate: 55, currency: 'USD', certifications: ['ASQ CCT', 'GMP Training'] },
      { roleId: 'maintenance_technician', name: 'Maintenance Technician', hourlyRate: 50, currency: 'USD', certifications: ['GMP Training', 'Equipment Specific'] },
      { roleId: 'validation_engineer', name: 'Validation Engineer', hourlyRate: 85, currency: 'USD', certifications: ['ASQ CQE', 'GMP Training', 'Validation Certification'] },
      { roleId: 'quality_assurance', name: 'Quality Assurance Specialist', hourlyRate: 65, currency: 'USD', certifications: ['ASQ CQA', 'GMP Training'] },
      { roleId: 'process_analyst', name: 'Process Analyst', hourlyRate: 70, currency: 'USD', certifications: ['Six Sigma', 'GMP Training'] },
      { roleId: 'quality_director', name: 'Quality Director', hourlyRate: 125, currency: 'USD', certifications: ['ASQ CMQ/OE', 'Regulatory Affairs'] },
      { roleId: 'contractor_validation', name: 'Contract Validation Specialist', hourlyRate: 95, currency: 'USD', certifications: ['GMP Training'] },
      { roleId: 'contractor_calibration', name: 'Contract Calibration Service', hourlyRate: 75, currency: 'USD', certifications: ['ISO 17025'] },
    ],
    overtimeMultiplier: 1.5,
    emergencyMultiplier: 2.0,
    weekendMultiplier: 1.5,
  },
}
